import React, { useState, useContext } from "react";
import { HiOutlineMail, HiOutlineLockClosed } from "react-icons/hi";
import { useNavigate, Link } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { AppContext } from "../../utils/state";
import { login, setAccessToken } from "../../utils/api";
import SnackbarAlert from "../common/sanckbar";
import "./login.scss";

interface AppState {
  accessToken?: string;
  user?: any;
  isAuthenticated: boolean;
}

interface AppContextType {
  state: AppState;
  dispatch: (action: { type: string; data: any }) => void;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useContext(
    AppContext as React.Context<AppContextType>
  );

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning" as "warning" | "error" | "success" | "info",
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setSnackbar({
        open: true,
        message: "Please fill in all fields",
        severity: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await login("login", { email, password });

      if (response && response.accessToken) {
        localStorage.setItem("accessToken", `JWT ${response.accessToken}`);
        setAccessToken(`JWT ${response.accessToken}`);

        state.accessToken = response.accessToken;
        state.user = response.user;
        state.isAuthenticated = true;
        dispatch({
          type: "setAuth",
          data: true,
        });

        setSnackbar({
          open: true,
          message: response.message || "Login successful",
          severity: "success",
        });
        setTimeout(() => {
          navigate("/products/list");
        }, 1000);
      } else {
        setSnackbar({
          open: true,
          message: (response && response.message) || "Login failed",
          severity: "warning",
        });
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Login failed. Please try again.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      console.error("Login error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className="login-page">
      <SnackbarAlert
        open={snackbar.open}
        closeSnackbar={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
      <div className="left-section">
        <div className="logo">
          <span className="logo-icon">DC</span>
        </div>
        <h1>Delnie Cloud</h1>
        <p>
          Efficiently manage master data with centralized control, role-level
          security, and seamless API creation. Integrate with MongoDB Charts for
          real-time analytics and customized insights, tailored to your business
          needs.
        </p>
      </div>
      <div className="right-section">
        <div className="portal-info">
          <span className="portal-icon">🖥️</span>
          <div>
            <p className="portal-title">ACCERON Client Portal</p>
            <p className="portal-subtitle">Powered by Delnie Cloud Admin</p>
          </div>
        </div>
        <h2>Welcome back</h2>
        <p className="subtitle">Enter your credentials to access your account</p>
        <form onSubmit={handleSignIn}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <HiOutlineMail className="input-icon" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                aria-label="Email address"
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <HiOutlineLockClosed className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                aria-label="Password"
              />
            </div>
            <div className="password-options">
              <span onClick={togglePasswordVisibility} className="show-password">
                {showPassword ? "HIDE" : "SHOW"}
              </span>
              <Link to="/forgot-password" className="forgot-password">
                Forgot password?
              </Link>
            </div>
          </div>
          <br />
          <button type="submit" className="sign-in-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <CircularProgress
                  size={20}
                  style={{ color: "#fff", marginRight: "8px" }}
                />
                Logging in...
              </>
            ) : (
              "Sign in"
            )}
          </button>
        </form>
        <p className="create-account">
          Don't have an account? <Link to="/register">Create account</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
